import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Check, X, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TYPE_LABEL = { status: "Estado ✓ / ✕", dropdown: "Desplegable", numeric: "Numérico" };
const COL_LABEL = ["Izquierda", "Centro", "Derecha"];

/** Iter 171 — Configuración → Checklist: campos autogestionables del Checklist de reparación. */
export default function ChecklistFieldsTab() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingOpts, setEditingOpts] = useState(null); // field id

  const load = () => api.get("/settings/checklist-fields").then((r) => setFields(r.data.fields || [])).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const patch = async (id, data, msg) => {
    try {
      const r = await api.put(`/settings/checklist-fields/${id}`, data);
      setFields((fs) => fs.map((f) => (f.id === id ? { ...f, ...r.data } : f)));
      if (msg) toast.success(msg);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (idx, dir) => {
    // Mover dentro de la misma columna (el orden global se mantiene coherente)
    const col = fields[idx].col ?? 0;
    let j = idx + dir;
    while (j >= 0 && j < fields.length && (fields[j].col ?? 0) !== col) j += dir;
    if (j < 0 || j >= fields.length) return;
    const next = fields.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setFields(next);
    try { await api.put("/settings/checklist-fields/reorder", { ids: next.map((f) => f.id) }); } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  if (loading) return <div className="p-6 text-sm text-[#525252]">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="checklist-fields-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">// Configuración</div>
          <h2 className="font-display text-xl font-black">Checklist de reparación</h2>
          <p className="text-sm text-[#525252] mt-1 max-w-2xl">
            Campos del panel "Montaje del Equipo y Pruebas". El tipo <b>Estado ✓ / ✕</b> usa la lógica actual (✕ = incidencia negativa).
            Desactivar un campo lo oculta sin borrar los valores ya guardados. Cada campo tiene un ID interno estable.
          </p>
        </div>
        <button type="button" onClick={() => setCreating(true)} data-testid="checklist-field-new"
          className="inline-flex items-center gap-2 bg-[#111110] hover:bg-[#6E8B3D] text-white px-4 py-2 text-sm font-semibold transition-colors">
          <Plus size={14} /> Nuevo campo
        </button>
      </div>

      {creating && <NewFieldForm onClose={() => setCreating(false)} onCreated={(f) => { setFields((fs) => [...fs, f]); setCreating(false); }} />}

      <div className="ds-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="checklist-fields-table">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b border-[#E5E5E0]">
              <th className="px-3 py-2.5 w-20">Orden</th><th className="px-3 py-2.5">Campo</th><th className="px-3 py-2.5">Tipo</th>
              <th className="px-3 py-2.5">Columna</th><th className="px-3 py-2.5">Opciones</th><th className="px-3 py-2.5 text-center">Oblig.</th>
              <th className="px-3 py-2.5 text-center">Notas</th><th className="px-3 py-2.5 text-center">Activo</th><th className="px-3 py-2.5 text-right">Usado</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map((col) => {
              const colIdx = fields.map((f, i) => ((f.col ?? 0) === col ? i : -1)).filter((i) => i >= 0);
              return [
                <tr key={`h${col}`} className="bg-[#F7F7F4] border-b border-[#E5E5E0]"><td colSpan={9} className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[#525252]">Columna {COL_LABEL[col]} · {colIdx.length} campos</td></tr>,
                ...colIdx.map((i, k) => (
                  <FieldRow key={fields[i].id} f={fields[i]} i={i} pos={k + 1} first={k === 0} last={k === colIdx.length - 1} onMove={move} onPatch={patch}
                    editingOpts={editingOpts === fields[i].id} onToggleOpts={() => setEditingOpts(editingOpts === fields[i].id ? null : fields[i].id)} />
                )),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Toggle({ on, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!on)} data-testid={testid} aria-pressed={on}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? "bg-[#6E8B3D]" : "bg-[#D5D7CE]"}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
  );
}

function FieldRow({ f, i, pos, first, last, onMove, onPatch, editingOpts, onToggleOpts }) {
  const [label, setLabel] = useState(f.label);
  useEffect(() => setLabel(f.label), [f.label]);
  const saveLabel = () => { if (label.trim() && label !== f.label) onPatch(f.id, { label: label.trim() }, "Nombre actualizado"); };
  return (
    <>
      <tr className={`border-b border-[#E5E5E0] ${f.active ? "" : "opacity-50"}`} data-testid={`checklist-field-${f.id}`}>
        <td className="px-3 py-1.5">
          <div className="flex items-center gap-1 font-mono text-xs">
            <span className="w-5">{pos}</span>
            <button type="button" onClick={() => onMove(i, -1)} disabled={first} className="p-0.5 hover:bg-[#F7F7F4] disabled:opacity-30" data-testid={`checklist-field-up-${f.id}`}><ArrowUp size={12} /></button>
            <button type="button" onClick={() => onMove(i, 1)} disabled={last} className="p-0.5 hover:bg-[#F7F7F4] disabled:opacity-30" data-testid={`checklist-field-down-${f.id}`}><ArrowDown size={12} /></button>
          </div>
        </td>
        <td className="px-3 py-1.5">
          <input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={saveLabel} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            data-testid={`checklist-field-label-${f.id}`} className="w-full bg-transparent border-b border-transparent hover:border-[#D5D7CE] focus:border-[#6E8B3D] outline-none py-0.5" />
          <div className="font-mono text-[10px] text-[#8A9098]">id: {f.id}</div>
        </td>
        <td className="px-3 py-1.5 text-xs">{TYPE_LABEL[f.type] || f.type}</td>
        <td className="px-3 py-1.5">
          <select value={f.col ?? 0} onChange={(e) => onPatch(f.id, { col: Number(e.target.value) }, "Columna actualizada")} data-testid={`checklist-field-col-${f.id}`}
            className="text-xs bg-transparent border border-[#E5E5E0] px-1 py-0.5">
            {COL_LABEL.map((c, k) => <option key={k} value={k}>{c}</option>)}
          </select>
        </td>
        <td className="px-3 py-1.5 text-xs">
          {f.type === "dropdown" ? (
            <button type="button" onClick={onToggleOpts} className="inline-flex items-center gap-1 underline decoration-dotted" data-testid={`checklist-field-opts-${f.id}`}>
              <Settings2 size={12} /> {(f.options || []).length} opciones
            </button>
          ) : "—"}
        </td>
        <td className="px-3 py-1.5 text-center"><Toggle on={!!f.required} onChange={(v) => onPatch(f.id, { required: v }, v ? "Campo obligatorio" : "Campo opcional")} testid={`checklist-field-required-${f.id}`} /></td>
        <td className="px-3 py-1.5 text-center"><Toggle on={!!f.allow_notes} onChange={(v) => onPatch(f.id, { allow_notes: v }, v ? "Notas permitidas" : "Notas desactivadas")} testid={`checklist-field-notes-${f.id}`} /></td>
        <td className="px-3 py-1.5 text-center"><Toggle on={!!f.active} onChange={(v) => onPatch(f.id, { active: v }, v ? "Campo activado" : "Campo desactivado (históricos conservados)")} testid={`checklist-field-active-${f.id}`} /></td>
        <td className="px-3 py-1.5 text-right font-mono text-xs text-[#525252]">{f.used_in || 0}</td>
      </tr>
      {editingOpts && (
        <tr className="border-b border-[#E5E5E0] bg-[#FAFAF8]"><td colSpan={9} className="px-3 py-3">
          <OptionsEditor options={f.options || []} onSave={(opts) => onPatch(f.id, { options: opts }, "Opciones guardadas").then(onToggleOpts)} onCancel={onToggleOpts} />
        </td></tr>
      )}
    </>
  );
}

export function OptionsEditor({ options, onSave, onCancel, onChange }) {
  const [opts, setOptsRaw] = useState(options.map((o) => ({ ...o })));
  // `onChange` (modo "nuevo campo"): el estado se propaga en vivo, sin botón Guardar.
  const setOpts = (next) => { const v = typeof next === "function" ? next(opts) : next; setOptsRaw(v); onChange?.(v.filter((o) => o.label.trim())); };
  const upd = (i, p) => setOpts(opts.map((o, k) => (k === i ? { ...o, ...p } : o)));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= opts.length) return; const n = opts.slice(); [n[i], n[j]] = [n[j], n[i]]; setOpts(n); };
  return (
    <div className="space-y-2" data-testid="checklist-options-editor">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Opciones del desplegable · "Negativa" = cuenta como incidencia del Checklist</div>
      {opts.map((o, i) => (
        <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`checklist-option-${i}`}>
          <button type="button" onClick={() => move(i, -1)} className="p-0.5"><ArrowUp size={12} /></button>
          <button type="button" onClick={() => move(i, 1)} className="p-0.5"><ArrowDown size={12} /></button>
          <input value={o.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Texto de la opción" data-testid={`checklist-option-label-${i}`}
            className="ds-card px-2 py-1 text-sm rounded-none w-64 outline-none focus:border-[#6E8B3D]" />
          <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={!!o.negative} onChange={(e) => upd(i, { negative: e.target.checked })} data-testid={`checklist-option-negative-${i}`} /> Negativa</label>
          <label className="inline-flex items-center gap-1 text-xs"><input type="checkbox" checked={o.active !== false} onChange={(e) => upd(i, { active: e.target.checked })} /> Activa</label>
          <button type="button" onClick={() => setOpts(opts.filter((_, k) => k !== i))} className="p-1 text-[#9B2C1F]"><X size={12} /></button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setOpts([...opts, { value: "", label: "", active: true, negative: false }])} data-testid="checklist-option-add"
          className="inline-flex items-center gap-1 text-xs border border-[#E5E5E0] px-2 py-1 hover:border-[#111110]"><Plus size={12} /> Añadir opción</button>
        {!onChange && (
          <>
            <button type="button" onClick={() => onSave(opts.filter((o) => o.label.trim()))} data-testid="checklist-options-save"
              className="inline-flex items-center gap-1 text-xs bg-[#6E8B3D] text-white px-3 py-1 font-semibold"><Check size={12} /> Guardar opciones</button>
            <button type="button" onClick={onCancel} className="text-xs px-2 py-1">Cancelar</button>
          </>
        )}
      </div>
    </div>
  );
}

function NewFieldForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ label: "", type: "status", col: 0, required: false, allow_notes: false, options: [] });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post("/settings/checklist-fields", form);
      toast.success("Campo creado");
      onCreated(r.data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="ds-card p-4 space-y-3" data-testid="checklist-field-form">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="text-xs"><span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Nombre</span>
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="checklist-field-form-label" className="mt-1 w-full ds-card px-2 py-1.5 text-sm rounded-none outline-none focus:border-[#6E8B3D]" /></label>
        <label className="text-xs"><span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Tipo de campo</span>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="checklist-field-form-type" className="mt-1 w-full ds-card px-2 py-1.5 text-sm rounded-none">
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
        <label className="text-xs"><span className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Columna</span>
          <select value={form.col} onChange={(e) => setForm({ ...form, col: Number(e.target.value) })} data-testid="checklist-field-form-col" className="mt-1 w-full ds-card px-2 py-1.5 text-sm rounded-none">
            {COL_LABEL.map((c, k) => <option key={k} value={k}>{c}</option>)}
          </select></label>
        <div className="text-xs flex items-end gap-4 pb-1.5">
          <label className="inline-flex items-center gap-1"><input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} data-testid="checklist-field-form-required" /> Obligatorio</label>
          <label className="inline-flex items-center gap-1"><input type="checkbox" checked={form.allow_notes} onChange={(e) => setForm({ ...form, allow_notes: e.target.checked })} data-testid="checklist-field-form-notes" /> Permitir notas</label>
        </div>
      </div>
      {form.type === "dropdown" && (
        <OptionsEditor options={form.options} onChange={(opts) => setForm((f) => ({ ...f, options: opts }))} />
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs border border-[#E5E5E0]">Cancelar</button>
        <button type="button" onClick={submit} disabled={saving || !form.label.trim()} data-testid="checklist-field-form-save"
          className="px-4 py-1.5 text-xs bg-[#6E8B3D] text-white font-semibold disabled:bg-[#A8A8A0]">{saving ? "Guardando…" : "Crear campo"}</button>
      </div>
    </div>
  );
}
